import api from "../../services/baseapi";

// ── Enums ─────────────────────────────────────────────────────────────────────

export type ReferenceType = "user_created" | "imported_standard";
export type ReferenceCategory =
  | "benchmark_value"
  | "lookup_table"
  | "indicator_framework"
  | "classification"
  | "other";

// ── Value shapes ──────────────────────────────────────────────────────────────

export interface BenchmarkValueContent {
  value: number;
  unit: string;
  operator?: "gte" | "lte" | "eq" | "gt" | "lt";
  source_text?: string;
  source_url?: string;
}

export interface IndicatorFrameworkRow {
  name: string;
  description?: string;
  default_value?: string;
}

// ── Core types ────────────────────────────────────────────────────────────────

export interface LibraryReference {
  id: string;
  project_id: string;
  type: ReferenceType;
  category: ReferenceCategory;
  title: string;
  description?: string | null;
  value: any;
  source_framework?: string | null;
  source_indicator_id?: string | null;
  source_framework_id?: string | null;
  citation?: string | null;
  created_by?: number | null;
  created_at: string;
  updated_at?: string;
  linked_count?: number;
}

export interface GlobalFramework {
  id: string;
  code: string;
  name: string;
  issuing_body: string;
  description: string;
  framework_type?: string;
  sector_tags: string[];
  indicator_count: number;
  last_updated: string;
  imported_count?: number | null;
}

export interface FrameworkIndicator {
  id: string;
  indicator_code: string;
  name: string;
  description?: string;
  default_benchmark?: number | null;
  unit?: string;
  imported?: boolean | null;
  already_imported?: boolean;
}

export interface ImportedGroup {
  framework_code: string;
  framework_name: string;
  framework_issuing_body: string;
  framework_id: string;
  items: LibraryReference[];
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function mapRef(r: any): LibraryReference {
  return { ...r, id: r.reference_id ?? r.id };
}

const BASE = (projectId: string) => `/library/projects/${projectId}`;

// ── My References ─────────────────────────────────────────────────────────────

export const listReferences = async (
  projectId: string,
  params?: {
    type?: ReferenceType;
    category?: ReferenceCategory;
    search?: string;
    skip?: number;
    limit?: number;
  },
): Promise<{ total: number; items: LibraryReference[] }> => {
  const { data } = await api.get(`${BASE(projectId)}/references`, { params });
  const items = Array.isArray(data) ? data : (data.items ?? []);
  return { total: data.total ?? items.length, items: items.map(mapRef) };
};

export const createReference = async (
  projectId: string,
  payload: {
    title: string;
    category: ReferenceCategory;
    description?: string;
    value?: string;
    file?: File;
    citation?: string;
    date?: string;
  },
): Promise<LibraryReference> => {
  const form = new FormData();
  form.append("title", payload.title);
  form.append("category", payload.category);
  if (payload.description) form.append("description", payload.description);
  if (payload.citation) form.append("citation", payload.citation);
  if (payload.date) form.append("date", payload.date);
  if (payload.file) {
    form.append("file", payload.file);
  } else if (payload.value) {
    form.append("value", payload.value);
  }
  const { data } = await api.post(`${BASE(projectId)}/references`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return mapRef(data);
};

export const updateReference = async (
  projectId: string,
  referenceId: string,
  payload: {
    title?: string;
    category?: ReferenceCategory;
    description?: string | null;
    value?: any;
    citation?: string | null;
    date?: string;
  },
): Promise<LibraryReference> => {
  const { data } = await api.patch(
    `${BASE(projectId)}/references/${referenceId}`,
    payload,
  );
  return mapRef(data);
};

export const deleteReference = async (
  projectId: string,
  referenceId: string,
): Promise<void> => {
  await api.delete(`${BASE(projectId)}/references/${referenceId}`);
};

// ── Global Standards ──────────────────────────────────────────────────────────

export const listFrameworks = async (params?: {
  search?: string;
  issuing_body?: string;
  sector?: string;
  framework_type?: string;
  year?: number | string;
  project_id?: string;
  skip?: number;
  limit?: number;
}): Promise<{ total: number; items: GlobalFramework[] }> => {
  const { data } = await api.get(`/library/frameworks`, { params });
  const items = Array.isArray(data) ? data : (data.items ?? []);
  return { total: data.total ?? items.length, items };
};

export const getFrameworkDetail = async (
  frameworkId: string,
  projectId?: string,
): Promise<{ framework: GlobalFramework; indicators: FrameworkIndicator[] }> => {
  const { data } = await api.get(`/library/frameworks/${frameworkId}`, {
    params: projectId ? { project_id: projectId } : undefined,
  });
  const indicators: FrameworkIndicator[] = (data.indicators ?? []).map((i: any) => ({
    ...i,
    already_imported: i.imported === true,
  }));
  const framework: GlobalFramework = {
    ...data,
    sector_tags: data.sector_tags ?? data.sectors ?? [],
  };
  return { framework, indicators };
};

// ── Import ────────────────────────────────────────────────────────────────────

export const importIndicators = async (
  projectId: string,
  frameworkId: string,
  indicatorIds: string[],
): Promise<{ imported_count: number; skipped_count: number }> => {
  const { data } = await api.post(
    `${BASE(projectId)}/frameworks/${frameworkId}/import`,
    { indicator_ids: indicatorIds },
  );
  return data;
};

export const listImported = async (
  projectId: string,
  params?: { search?: string },
): Promise<ImportedGroup[]> => {
  const { data } = await api.get(`${BASE(projectId)}/imported`, { params });
  return (data.groups ?? []).map((g: any) => ({
    ...g,
    items: (g.items ?? []).map(mapRef),
  }));
};

export const unimportReferences = async (
  projectId: string,
  referenceIds: string[],
): Promise<{ removed_count: number; skipped_count: number }> => {
  const { data } = await api.post(`${BASE(projectId)}/unimport`, {
    reference_ids: referenceIds,
  });
  return data;
};

export const downloadReferenceFile = async (
  projectId: string,
  referenceId: string,
): Promise<void> => {
  const { data, headers } = await api.get(
    `${BASE(projectId)}/references/${referenceId}/file`,
    { responseType: "blob" },
  );
  const cd: string = headers["content-disposition"] ?? "";
  const match = /filename\*?=(?:UTF-8'')?("?)([^";]+)\1/i.exec(cd);
  const filename = match ? decodeURIComponent(match[2]) : "download";
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
